import { jsx, jsxs } from 'react/jsx-runtime';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { EGDSField } from '../../field/field/index.esm.js';
import '../../field/field-label/index.esm.js';
import '../../field/field-message/index.esm.js';
import { EGDSIcon, EGDSIconSize } from '../../icons/icon/index.esm.js';
import '../../utils/index_orig/index.esm.js';
import '../../utils/theme/theme-provider/index.esm.js';
import styles from '../../styles/uitk-egds-input.css';
import { classNames } from '../../utils/classnames/index.esm.js';
const getClassNameList = classNames.bind(styles);
class Input extends React.Component {
  static defaultProps = {
    hasClearButton: false,
    required: false,
    // eslint-disable-next-line react/default-props-match-prop-types -- Applied to avoid a breaking change. Must be fixed ASAP with https://github.expedia.biz/egds/uitk-react/issues/6520
    value: ''
  };
  autofillInput;
  inputRef;
  id; // Unique ID if not provided for label a11y
  constructor(props) {
    super(props);
    this.state = {
      autofilled: false,
      mounted: false
    };
    this.handleClear = this.handleClear.bind(this);
    this.setAutoFill = this.setAutoFill.bind(this);
    this.inputRef = props.domRef || React.createRef();
    this.id = props.id ?? `input-${Math.random().toString(36).substring(2, 9)}` ?? undefined; // Can replace with useId when upgrading to React 18
  }
  componentDidMount() {
    this.startTimer();
    this.setState({
      mounted: true
    });
  }
  componentWillUnmount() {
    const {
      domRef
    } = this.props;
    if (domRef) {
      this.clearTimer();
    }
  }
  handleClear() {
    const {
      handleClear,
      onChange,
      name
    } = this.props;
    if (onChange) {
      onChange({
        target: {
          value: '',
          name: name ?? this.id
        }
      });
      // Shift focus back to input field for a11y
      this.inputRef?.current?.focus();
    }
    if (typeof handleClear === 'function') {
      handleClear();
    }
  }
  setAutoFill() {
    const curr = this.inputRef?.current;
    if (curr) {
      try {
        if (curr.matches(':autofill')) {
          this.clearTimer();
          this.setState({
            autofilled: true
          });
        }
      } catch (error) {
        try {
          if (curr.matches(':-webkit-autofill')) {
            this.clearTimer();
            this.setState({
              autofilled: true
            });
          }
          // eslint-disable-next-line @typescript-eslint/no-shadow
        } catch (error) {
          return false;
        }
      }
    }
  }
  startTimer() {
    // Before init setTimeout, clear the existing timer
    this.clearTimer();
    this.autofillInput = setTimeout(() => {
      this.setAutoFill();
    }, 500);
  }
  clearTimer() {
    clearTimeout(this.autofillInput);
  }
  render() {
    // Black list these props from the input field
    const {
      children,
      className,
      label,
      hasBorder,
      hasFloatedLabel,
      hasVisibleLabel,
      icon,
      instruction,
      invalid,
      l10n,
      hasClearButton,
      required,
      clearButtonLabel,
      ...attributes
    } = this.props;
    const {
      placeholder,
      ...attrs
    } = attributes;
    const {
      disabled,
      readOnly,
      value
    } = attributes;
    const {
      autofilled,
      mounted
    } = this.state;
    const showClearButton = mounted && hasClearButton && value;
    const fieldPropsClassNames = getClassNameList(showClearButton && 'has-input-cta');
    const fieldProps = {
      labelClassName: fieldPropsClassNames,
      disabled,
      hasAutofill: autofilled,
      hasBorder,
      hasFloatedLabel,
      hasVisibleLabel,
      icon,
      id: this.id,
      instruction,
      invalid,
      label,
      placeholder,
      readOnly,
      required
    };
    const inputExtraAttr = attrs;
    if (inputExtraAttr.domRef) {
      delete inputExtraAttr.domRef;
    }
    if (inputExtraAttr.handleClear) {
      delete inputExtraAttr.handleClear;
    }
    const cssClassNames = getClassNameList('uitk-field-input', showClearButton && 'has-input-cta', className, !placeholder && !value?.toString() && 'empty-placeholder');
    let ariaFieldLabel = clearButtonLabel || label;
    if (React.isValidElement(label)) {
      const labelProp = label.props;
      ariaFieldLabel = labelProp && labelProp.children && labelProp.children[0];
    }
    const localizedClearText = l10n.formatText('clear');
    const iconTitle = [localizedClearText, ariaFieldLabel].join(' ');
    return jsxs(EGDSField, {
      ...fieldProps,
      children: [jsx("input", {
        ...inputExtraAttr,
        className: cssClassNames,
        "aria-describedby": invalid ? `${this.id}-error` : undefined,
        placeholder: placeholder,
        ref: this.inputRef,
        id: this.id,
        "aria-required": required,
        "aria-invalid": !!invalid
      }), children, showClearButton && mounted && jsx("button", {
        className: getClassNameList('uitk-clear-button', 'uitk-input-cta'),
        onClick: this.handleClear,
        type: "button",
        children: jsx(EGDSIcon, {
          name: "cancel",
          size: EGDSIconSize.SMALL,
          title: iconTitle,
          id: `${this.id}-clear-input-button`
        })
      })]
    });
  }
}
const EGDSInput = props => {
  const l10n = useLocalization();
  return jsx(Input, {
    l10n: l10n,
    ...props
  });
};
export { EGDSInput };